import { get } from 'lodash'
import { i18n } from '@/plugins/i18n/i18n'
import { GetSignatureRecordMappingSignatureLink } from '@/api/signatureRecordMapping'
import { useShop } from '@/use/shop'

/**
 * 同意書記錄工具函式
 * 提供同意書記錄相關的共用邏輯
 *
 * @example
 * ```js
 * import { useSignatureRecordUtils } from '@/use/useSignatureRecord'
 *
 * setup() {
 *   const { getOrderCode, getCopyLinkStatus, copySignatureLink } = useSignatureRecordUtils()
 *
 *   const orderCode = getOrderCode(record)
 *   const { canShow, isDisabled } = getCopyLinkStatus(record)
 *   await copySignatureLink(record.id)
 * }
 * ```
 */
const copy = async (text) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text)
  } else {
    const el = document.createElement('textarea')
    el.value = text
    el.style.cssText = 'position:fixed;opacity:0'
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
  }
}

export const useSignatureRecordUtils = () => {
  const { shopId } = useShop()

  /**
   * 從同意書記錄中提取訂單編號
   * 支援多種來源：預約訂單、會員商城、點數兌換、第三方消費
   *
   * @param {Object} record - 同意書記錄物件
   * @param {Object} [record.AppointmentOrder] - 預約訂單物件
   * @param {string} [record.AppointmentOrder.code] - 預約訂單編號
   * @param {Object} [record.MemberStoreOrder] - 會員商城訂單物件
   * @param {string} [record.MemberStoreOrder.code] - 會員商城訂單編號
   * @param {Object} [record.MemberStoreOrderPointExchange] - 點數兌換訂單物件
   * @param {string} [record.MemberStoreOrderPointExchange.code] - 點數兌換訂單編號
   * @param {Object} [record.MemberExternalTransaction] - 第三方消費物件
   * @param {string} [record.MemberExternalTransaction.transactionId] - 第三方交易ID
   * @returns {string} 訂單編號，若無則回傳 '-'
   */
  const getOrderCode = (record) => {
    return get(record, 'AppointmentOrder.code') ||
      get(record, 'MemberStoreOrder.code') ||
      get(record, 'MemberStoreOrderPointExchange.code') ||
      get(record, 'MemberExternalTransaction.transactionId') ||
      '-'
  }

  /**
   * 取得複製連結按鈕的顯示狀態
   * 僅手動發放（manual）和第三方消費（memberExternalTransaction）來源支援複製連結
   *
   * @param {Object} record - 同意書記錄物件
   * @param {string} record.sourceType - 來源類型 (manual|memberExternalTransaction|appointmentOrder|memberStoreOrder|memberStoreOrderPointExchange)
   * @param {string} record.status - 簽署狀態 (unsigned|signed|expired)
   * @returns {Object} 複製連結狀態
   * @returns {boolean} canShow - 是否顯示複製連結按鈕
   * @returns {boolean} isDisabled - 複製連結按鈕是否禁用（已逾時）
   */
  const getCopyLinkStatus = (record) => {
    const sourceType = get(record, 'sourceType')
    const status = get(record, 'status')
    const isManualOrExternal = ['manual', 'memberExternalTransaction'].includes(sourceType)

    return {
      canShow: isManualOrExternal && ['unsigned', 'expired'].includes(status),
      isDisabled: isManualOrExternal && status === 'expired',
    }
  }

  /**
   * 複製同意書簽署連結到剪貼簿
   *
   * @param {string} recordId - 同意書記錄ID
   * @returns {Promise<void>}
   * @throws {Error} 複製失敗時拋出例外狀況
   */
  const copySignatureLink = async (recordId) => {
    try {
      const [linkRes, err] = await GetSignatureRecordMappingSignatureLink({
        shopId: shopId.value,
        id: recordId,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      if (!linkRes?.signatureLink) {
        window.$message.error(i18n.t('member.signatureRecord.error.cannotGetLink'))
        return
      }

      await copy(linkRes.signatureLink)
      window.$message.success(i18n.t('common.message.copySuccess'))
    } catch (error) {
      window.$message.error(i18n.t('member.signatureRecord.error.copyFailed'))
      throw error
    }
  }

  return {
    getOrderCode,
    getCopyLinkStatus,
    copySignatureLink,
  }
}
